import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Observable, merge } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import produce from 'immer';
import { Patient, mapPatient, mapName } from '../../models';
import { ApiService, EnvService, SentryService } from '../../services';
import {
  ApiStoreData,
  MissingApiRequestDataError,
  defaultApiStoreIterableData,
  CcsSentryError,
  defaultApiStoreData,
} from '../utils';
import { FetchPatientInsurances } from '../patient-insurances/patient-insurances.actions';
import { FetchPatientAddresses } from '../addresses/addresses.actions';
import { FetchPatientPaymentMethods } from '../payment-methods/payment-methods.actions';
import {
  LoginPatient,
  LoadPatientData,
  UpdatePatient,
  SetPatientName,
  SetPatientConsent,
  GetPatients,
  FetchPreviewPatient,
  SetSelectedPatientId,
  SetLastSelectedPatientId,
  SetPreferredLogin,
  SetPreferredCodeMethod,
} from './patient.actions';
import { TestPatientsState, TestPatientsStateModel } from '../test-patients/test-patients.state';
import { AgentState, AgentStateModel } from '../agent/agent.state';
import { SignInState } from 'src/app/sign-in/sign-in.component';

export interface PatientStateModel {
  lastSelectedPatientId: number | null;
  loginPatient: ApiStoreData<null>;
  patients: ApiStoreData<Patient[]>;
  pwpUserId: number | null;
  selectedPatientId: number | null;
  sessionId: number | null;
  updatePatient: ApiStoreData<null>;
  preferredLogin: SignInState;
  preferredCodeMethod: 'email' | 'phone';
}

export const patientDefaults: PatientStateModel = {
  lastSelectedPatientId: null,
  loginPatient: defaultApiStoreData,
  patients: defaultApiStoreIterableData,
  pwpUserId: null,
  selectedPatientId: null,
  sessionId: null,
  updatePatient: defaultApiStoreData,
  preferredLogin: 'SEND_CODE',
  preferredCodeMethod: 'email',
};

@State<PatientStateModel>({
  name: 'patient',
  defaults: patientDefaults,
})
@Injectable({ providedIn: 'root' })
export class PatientState {
  constructor(
    private apiService: ApiService,
    private store: Store,
    private sentry: SentryService,
    private envService: EnvService
  ) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector([AgentState, TestPatientsState])
  static isPreviewMode(agentState: AgentStateModel, testPatientsState: TestPatientsStateModel): boolean {
    return !!agentState.agentPreviewPatientId || !!testPatientsState.testPatient.id;
  }

  @Selector()
  static lastSelectedPatientId(state: PatientStateModel): number | null {
    return state.lastSelectedPatientId;
  }

  @Selector()
  static patient(state: PatientStateModel): Patient | null {
    return state.selectedPatientId ? state.patients.data.find((p) => p.id === state.selectedPatientId) || null : null;
  }

  @Selector()
  static patientStateErrors(state: PatientStateModel): CcsSentryError[] | null {
    const errors = [state.loginPatient.error, state.patients.error].filter((e) => !!e);
    return errors.length > 0 ? (errors as CcsSentryError[]) : null;
  }

  @Selector()
  static patientStateIsLoading(state: PatientStateModel): boolean {
    return state.loginPatient.isLoading || state.patients.isLoading;
  }

  @Selector()
  static patientsState(state: PatientStateModel): ApiStoreData<Patient[]> {
    return state.patients;
  }

  @Selector()
  static selectedPatientId(state: PatientStateModel): number | null {
    return state.selectedPatientId;
  }

  @Selector()
  static selectedPatientIndex(state: PatientStateModel): number | null {
    return state.selectedPatientId
      ? state.patients.data.findIndex((p) => p.id === state.selectedPatientId) || null
      : null;
  }

  @Selector()
  static sessionId(state: PatientStateModel): number | null {
    return state.sessionId;
  }

  @Selector()
  static updatePatientState(state: PatientStateModel): ApiStoreData<null> {
    return state.updatePatient;
  }

  @Selector()
  static preferredLogin(state: PatientStateModel): string | null {
    return state.preferredLogin;
  }

  @Selector()
  static preferredCodeMethod(state: PatientStateModel): 'email' | 'phone' {
    return state.preferredCodeMethod;
  }

  private recordUser(patientId: number) {
    this.sentry.recordUser(patientId);

    if (crypto && crypto.subtle) {
      // Use the SubtleCrypto library to create a SHA-256 digest from the email + salt
      crypto.subtle
        .digest('SHA-256', new TextEncoder().encode(patientId + this.envService.googleAnalytics.hashSalt))
        .then((hashed) => {
          // The digest is returned in a wonky format so we need to convert it to a hex string
          let encryptedId = ([] as number[]).map
            .call(new Uint8Array(hashed), (b) => ('00' + b.toString(16)).slice(-2))
            .join('');

          // There is a bug in the library's gtag config() function. Using gtag on window straight.
          // TODO Refine this later by replacing the gtag library we are using
          (window as any).gtag('config', this.envService.googleAnalytics.measurementId, { user_id: encryptedId });
        });
    }
  }

  @Action(FetchPreviewPatient)
  fetchPreviewPatient(ctx: StateContext<PatientStateModel>, action: FetchPreviewPatient) {
    ctx.setState(
      produce((draft) => {
        draft.patients.isLoading = true;
      })
    );

    return this.apiService.getPatient(action.patientId).pipe(
      tap({
        next: (response) => {
          this.recordUser(action.patientId);

          const selectedPatient = mapPatient(response.data[0]);

          ctx.dispatch(new SetSelectedPatientId(selectedPatient.id));

          this.handleSuccess({ ctx, data: [selectedPatient], stateKey: 'patients' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'patients' }))
    );
  }

  @Action(GetPatients)
  getPatients(ctx: StateContext<PatientStateModel>, action: GetPatients) {
    ctx.setState(
      produce((draft) => {
        draft.patients.isLoading = true;
      })
    );

    return this.apiService.getPatients().subscribe({
      next: (response) => {
        const data = response.data.map((p) => mapPatient(p));

        this.handleSuccess({ ctx, stateKey: 'patients', data });

        if (data.length === 1) {
          this.store.dispatch(new LoginPatient(action.ipAddress, data[0].id));
        } else if (!!action.lastSelectedPatientId && data.some((p) => p.id == action.lastSelectedPatientId)) {
          this.store.dispatch(new LoginPatient(action.ipAddress, action.lastSelectedPatientId));
        }
      },
      error: () => {
        this.apiService.activateUser().subscribe({
          next: () => {
            this.apiService.getPatients().subscribe({
              next: (response) => {
                const data = response.data.map((p) => mapPatient(p));

                this.handleSuccess({ ctx, stateKey: 'patients', data });

                if (data.length === 1) {
                  this.store.dispatch(new LoginPatient(action.ipAddress, data[0].id));
                } else if (!!action.lastSelectedPatientId && data.some((p) => p.id == action.lastSelectedPatientId)) {
                  this.store.dispatch(new LoginPatient(action.ipAddress, action.lastSelectedPatientId));
                }
              },
              error: (error) => {
                this.handleError({ ctx, error, stateKey: 'patients' });
              },
            });
          },
          error: (error) => {
            this.handleError({ ctx, error, stateKey: 'patients' });
          },
        });
      },
    });
  }

  @Action(LoadPatientData)
  loadPatientData(ctx: StateContext<PatientStateModel>) {
    return merge(
      ctx.dispatch(new FetchPatientInsurances()),
      ctx.dispatch(new FetchPatientAddresses()),
      ctx.dispatch(new FetchPatientPaymentMethods())
    );
  }

  @Action(LoginPatient)
  loginPatient(ctx: StateContext<PatientStateModel>, action: LoginPatient) {
    ctx.setState(
      produce((draft) => {
        draft.loginPatient.isLoading = true;
      })
    );

    const state = ctx.getState();

    if (state.patients.data.length === 0) {
      const error = new MissingApiRequestDataError(['Missing patients data']);
      return this.handleError({ ctx, error, stateKey: 'loginPatient' });
    }

    if (!state.patients.data.some((p) => p.id === action.patientId)) {
      const error = new MissingApiRequestDataError(['Selected patient is not associated with the user']);
      return this.handleError({ ctx, error, stateKey: 'loginPatient' });
    }

    return this.apiService
      .loginPatient({
        ...action,
      })
      .pipe(
        tap({
          next: (response) => {
            const { id, sessionId } = response.data;

            this.recordUser(action.patientId);

            ctx.setState(
              produce((draft) => {
                draft.lastSelectedPatientId = action.patientId;
                draft.pwpUserId = id;
                draft.selectedPatientId = action.patientId;
                draft.sessionId = sessionId || null;
              })
            );

            this.handleSuccess({ ctx, stateKey: 'loginPatient' });
          },
        }),
        catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'user' }))
      );
  }

  @Action(SetLastSelectedPatientId)
  setLastSelectedPatientId(ctx: StateContext<PatientStateModel>, action: SetLastSelectedPatientId) {
    ctx.setState(
      produce((draft) => {
        draft.lastSelectedPatientId = action.patientId;
      })
    );
  }

  @Action(SetPatientConsent)
  setPatientConsent(ctx: StateContext<PatientStateModel>, action: SetPatientConsent) {
    ctx.setState(
      produce((draft) => {
        const patientIndex = draft.patients.data.findIndex((p) => p.id === action.patientId);
        if (patientIndex !== -1) {
          draft.patients.data[patientIndex].consentedToMarketing = action.consentedToMarketing;
        }
      })
    );
  }

  @Action(SetPatientName)
  setPatientName(ctx: StateContext<PatientStateModel>, action: SetPatientName) {
    ctx.setState(
      produce((draft) => {
        const patientIndex = draft.patients.data.findIndex((p) => p.id === action.data.patientId);
        if (patientIndex !== -1) {
          draft.patients.data[patientIndex] = {
            ...draft.patients.data[patientIndex],
            ...mapName(action.data.firstName, action.data.lastName),
          };
        }
      })
    );
  }

  @Action(SetSelectedPatientId)
  setSelectedPatientId(ctx: StateContext<PatientStateModel>, action: SetSelectedPatientId) {
    ctx.setState(
      produce((draft) => {
        draft.selectedPatientId = action.patientId;
      })
    );
  }

  @Action(UpdatePatient)
  updatePatient(ctx: StateContext<PatientStateModel>, action: UpdatePatient) {
    const state = ctx.getState();

    if (!state.selectedPatientId) {
      const error = new MissingApiRequestDataError(['Missing selectedPatientId']);
      return this.handleError({ ctx, error, stateKey: 'updatePatient', updating: true });
    }

    if (state.patients.data.length === 0) {
      const error = new MissingApiRequestDataError(['Missing patients data']);
      return this.handleError({ ctx, error, stateKey: 'updatePatient', updating: true });
    }

    const patient = state.patients.data.find((p) => p.id === state.selectedPatientId);

    if (!patient) {
      const error = new MissingApiRequestDataError([
        'SelectedPatientId does not match any associated patients for the user',
      ]);
      return this.handleError({ ctx, error, stateKey: 'updatePatient', updating: true });
    }

    const {
      consentedToMarketing: currentMarketingConsentValue,
      acceptedTermsAndConditions: currentTermsAndConditionsValue,
      refusedGenericMedications: currentGenericMedicationsRefusalValue,
    } = patient;

    // Optionally we can have optimistic Update
    ctx.setState(
      produce((draft) => {
        draft.updatePatient.isUpdating = true;

        const patient = draft.patients.data.find((p) => p.id === state.selectedPatientId);

        if (action.options.optimisticUpdate) {
          if (typeof action.formValues.consentedToMarketing === 'boolean') {
            patient!.consentedToMarketing = action.formValues.consentedToMarketing;
          }

          if (typeof action.formValues.acceptedTermsAndConditions === 'boolean') {
            patient!.acceptedTermsAndConditions = action.formValues.acceptedTermsAndConditions;
          }

          if (typeof action.formValues.refusedGenericMedications === 'boolean') {
            patient!.refusedGenericMedications = action.formValues.refusedGenericMedications;
          }
        }
      })
    );

    return this.apiService.updatePatient(state.selectedPatientId, action.formValues).pipe(
      tap({
        next: (response) => {
          const updateStateFunction = (draft: PatientStateModel) => {
            if (action.options.optimisticUpdate) {
              const patient = draft.patients.data.find((p) => p.id === state.selectedPatientId);

              if (typeof action.formValues.consentedToMarketing === 'boolean') {
                patient!.consentedToMarketing = action.formValues.consentedToMarketing;
              }

              if (typeof action.formValues.acceptedTermsAndConditions === 'boolean') {
                patient!.acceptedTermsAndConditions = action.formValues.acceptedTermsAndConditions;
              }

              if (typeof action.formValues.refusedGenericMedications === 'boolean') {
                patient!.refusedGenericMedications = action.formValues.refusedGenericMedications;
              }
            } else {
              const patient = draft.patients.data.find((p) => p.id === state.selectedPatientId);
              const newPatientData = response.data.find((p) => p.patientId === state.selectedPatientId);

              if (typeof newPatientData?.consentedToMarketing === 'boolean') {
                patient!.consentedToMarketing = newPatientData?.consentedToMarketing;
              }

              if (typeof newPatientData?.acceptedTermsAndConditions === 'boolean') {
                patient!.acceptedTermsAndConditions = newPatientData?.acceptedTermsAndConditions;
              }

              if (typeof newPatientData?.refusedGenericMedications === 'boolean') {
                patient!.refusedGenericMedications = newPatientData?.refusedGenericMedications;
              }

              if (typeof newPatientData?.verifiedHipaa === 'boolean') {
                patient!.verifiedHipaa = newPatientData?.verifiedHipaa;
              }
            }
          };

          this.handleSuccess({ ctx, stateKey: 'updatePatient', updating: true, updateStateFunction });
        },
      }),
      catchError((error: Error): Observable<any> => {
        const updateStateFunction = (draft: PatientStateModel) => {
          if (action.options.optimisticUpdate) {
            const patient = draft.patients.data.find((p) => p.id === state.selectedPatientId);

            if (typeof action.formValues.consentedToMarketing === 'boolean') {
              patient!.consentedToMarketing = currentMarketingConsentValue;
            }

            if (typeof action.formValues.acceptedTermsAndConditions === 'boolean') {
              patient!.acceptedTermsAndConditions = currentTermsAndConditionsValue;
            }

            if (typeof action.formValues.refusedGenericMedications === 'boolean') {
              patient!.refusedGenericMedications = currentGenericMedicationsRefusalValue;
            }
          }
        };

        return this.handleError({ ctx, error, stateKey: 'updatePatient', updating: true, updateStateFunction });
      })
    );
  }

  @Action(SetPreferredLogin)
  setPreferredLogin(ctx: StateContext<PatientStateModel>, action: SetPreferredLogin) {
    ctx.setState(
      produce((draft) => {
        draft.preferredLogin = action.preferredLogin;
      })
    );
  }

  @Action(SetPreferredCodeMethod)
  setPreferredCodeMethod(ctx: StateContext<PatientStateModel>, action: SetPreferredCodeMethod) {
    ctx.setState(
      produce((draft) => {
        draft.preferredCodeMethod = action.preferredCodeMethod;
      })
    );
  }
}
